import React from "react";
import styled from "styled-components";
import { colors } from "src/theme/colors";
import { line, radii, shadow, withAlpha } from "src/theme/ui";

/**
 * SettingsRail — the grouped navigation down the left of a settings page.
 *
 * Ported from football's own rail, which is where this pattern is settled.
 * It does not draw one flat list: it files each section under the heading the
 * reader thinks about it by, and carries a SECOND LINE under a label saying
 * what that section currently holds.
 *
 * That detail line is the rail's whole value — the state of the account can be
 * read without opening a single section — so it must always be a real record
 * (how many bank accounts, which risk band) and never a fabricated count. A
 * section with nothing true to say gets no second line.
 *
 * It replaces an Ant Design inline Menu whose rows were 64px tall carrying
 * 16px semibold labels, whose selected row took a full brand-colour fill, and
 * whose submenus hid the bank accounts and the security sections one click
 * away — so the page could not say how many bank accounts there were without
 * being asked.
 */

export interface RailItem {
  /** The key handed back on select — the leaf key. */
  key: string;
  /** The section this leaf belongs to, for callers that switch on a group. */
  section?: string;
  label: string;
  /** The real second line. Omitted when there is nothing true to say. */
  detail?: string;
}

export interface RailGroup {
  label: string;
  items: RailItem[];
}

const Nav = styled.nav`
  width: 100%;
  padding: 8px;
`;

const Group = styled.div`
  margin-bottom: 4px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const GroupLabel = styled.div`
  padding: 13px 13px 7px;
  font-size: 9.5px;
  font-weight: 600;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: ${colors.gray[400]};
`;

const Entry = styled.button<{ $on: boolean }>`
  display: block;
  width: 100%;
  margin-bottom: 2px;
  padding: 9px 13px;
  border: 0;
  border-radius: 10px;
  text-align: left;
  cursor: pointer;
  background: ${({ $on }) =>
    $on ? withAlpha(colors.primary[500], 0.09) : "transparent"};
  transition: background 0.15s ease;

  &:hover {
    background: ${({ $on }) =>
      $on ? withAlpha(colors.primary[500], 0.09) : colors.gray[50]};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focusRing};
  }
`;

const EntryLabel = styled.span<{ $on: boolean }>`
  display: block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 13px;
  font-weight: ${({ $on }) => ($on ? 600 : 540)};
  color: ${({ $on }) => ($on ? colors.primary[600] : colors.gray[800])};
`;

const EntryDetail = styled.span<{ $on: boolean }>`
  display: block;
  margin-top: 3px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 11.5px;
  color: ${({ $on }) =>
    $on ? withAlpha(colors.primary[600], 0.75) : colors.gray[500]};
`;

const Rule = styled.div`
  margin: 6px 5px;
  border-top: 1px solid ${line.hairline};
`;

const SettingsRail: React.FC<{
  groups: RailGroup[];
  active: string;
  onSelect: (item: RailItem) => void;
  className?: string;
}> = ({ groups, active, onSelect, className }) => {
  const shown = groups.filter((group) => group.items.length > 0);

  return (
    <Nav className={className}>
      {shown.map((group, index) => (
        <Group key={group.label}>
          {index > 0 && <Rule />}
          <GroupLabel>{group.label}</GroupLabel>
          {group.items.map((item) => {
            const on = item.key === active;
            return (
              <Entry
                key={item.key}
                type="button"
                $on={on}
                aria-current={on ? "true" : undefined}
                onClick={() => onSelect(item)}
              >
                <EntryLabel $on={on}>{item.label}</EntryLabel>
                {item.detail && (
                  <EntryDetail $on={on}>{item.detail}</EntryDetail>
                )}
              </Entry>
            );
          })}
        </Group>
      ))}
    </Nav>
  );
};

export default SettingsRail;
